<template>
  <div class="page-wrapper">
    <main-navigation></main-navigation>

    <div class="bulk-upload-container">
      <!-- HEADER SECTION -->
      <div class="header-section">
        <div class="header-content">
          <div class="header-icon">
            <i class="bi bi-building"></i>
          </div>
          <div>
            <h1 class="header-title">Bulk Upload Tenants & Units</h1>
            <p class="header-subtitle">Upload tenants already staying in units with a single CSV file</p>
          </div>
        </div>
        <button class="btn-template" @click="downloadTemplate">
          <i class="bi bi-file-earmark-arrow-down"></i>
          Download Template
        </button>
      </div>

      <!-- MAIN CONTENT GRID -->
      <div class="content-grid">
        <!-- LEFT COLUMN - UPLOAD SECTION -->
        <div class="upload-section">
          <div class="upload-card">
            <div class="card-header">
              <i class="bi bi-cloud-upload"></i>
              <h3>Upload CSV File</h3>
            </div>

            <!-- HOUSE SELECTION -->
            <div class="house-selection">
              <label class="form-label">
                <i class="bi bi-house-door"></i>
                Select House
              </label>
              <select v-model="selectedHouseId" class="form-select" required>
                <option value="">-- Choose a house --</option>
                <option v-for="house in houses" :key="house.id" :value="house.id">
                  {{ house.name }}
                </option>
              </select>
            </div>

            <!-- FILE UPLOAD AREA -->
            <div class="file-upload-area">
              <label class="form-label">
                <i class="bi bi-file-earmark-spreadsheet"></i>
                CSV File
              </label>
              
              <div class="file-drop-zone" @click="triggerFileInput" @dragover.prevent @drop.prevent="handleDrop">
                <input type="file" ref="fileInput" class="d-none" accept=".csv" @change="handleFile" />
                
                <div v-if="!file" class="drop-content">
                  <div class="drop-icon">
                    <i class="bi bi-cloud-arrow-up"></i>
                  </div>
                  <div>
                    <p class="drop-text">Drop CSV file or <span class="browse-link">browse</span></p>
                    <p class="drop-hint">Supports .csv files with tenant and unit data</p>
                  </div>
                </div>
                
                <div v-else class="file-preview">
                  <div class="file-icon">
                    <i class="bi bi-file-earmark-spreadsheet"></i>
                  </div>
                  <div class="file-details">
                    <p class="file-name">{{ file.name }}</p>
                    <p class="file-info">{{ formatFileSize(file.size) }}</p>
                  </div>
                  <button class="btn-remove-file" @click.stop="removeFile">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <!-- CSV FORMAT GUIDE -->
            <div class="format-guide">
              <h4><i class="bi bi-info-circle"></i> CSV Format Requirements</h4>
              <p>Your CSV must include these columns (<span class="required-star">*</span> required):</p>
              
              <div class="columns-categories">
                <!-- Tenant Information -->
                <div class="category-section">
                  <h5><i class="bi bi-person"></i> Tenant Information</h5>
                  <div class="columns-grid">
                    <span class="column-tag required">
                      <i class="bi bi-asterisk"></i> tenant_name
                    </span>
                    <span class="column-tag required">
                      <i class="bi bi-asterisk"></i> tenant_phone
                    </span>
                    <span class="column-tag required">
                      <i class="bi bi-asterisk"></i> tenant_idnumber
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-gender-ambiguous"></i> gender
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-heart"></i> marital_status
                    </span>
                  </div>
                </div>

                <!-- Unit Information -->
                <div class="category-section">
                  <h5><i class="bi bi-door-open"></i> Unit Information</h5>
                  <div class="columns-grid">
                    <span class="column-tag required">
                      <i class="bi bi-asterisk"></i> unit_number
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-tag"></i> unit_type
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-currency-dollar"></i> monthly_rent
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-tools"></i> service_charge
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-plus-circle"></i> other_charges
                    </span>
                  </div>
                </div>

                <!-- Financial Information -->
                <div class="category-section">
                  <h5><i class="bi bi-cash-stack"></i> Financial Information</h5>
                  <div class="columns-grid">
                    <span class="column-tag">
                      <i class="bi bi-exclamation-triangle"></i> arrears
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-wallet2"></i> wallet_amount
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-cash"></i> deposit_paid
                    </span>
                  </div>
                </div>

                <!-- Meter Readings -->
                <div class="category-section">
                  <h5><i class="bi bi-water"></i> Utilities</h5>
                  <div class="columns-grid">
                    <span class="column-tag">
                      <i class="bi bi-droplet"></i> water_meter_readings
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-lightning"></i> electricity_meter_readings
                    </span>
                    <span class="column-tag">
                      <i class="bi bi-calendar"></i> move_in_date
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <!-- PREVIEW TABLE -->
            <div v-if="filePreview.length > 0" class="preview-section">
              <div class="preview-header">
                <i class="bi bi-table"></i>
                <span>Data Preview ({{ filePreview.length }} records)</span>
              </div>
              <div class="preview-table-container">
                <table class="preview-table dash-table">
                  <thead>
                    <tr>
                      <th>Unit</th>
                      <th>Tenant</th>
                      <th>Phone</th>
                      <th>ID</th>
                      <th>Unit Type</th>
                      <th>Rent</th>
                      <th>Arrears</th>
                      <th>Wallet</th>
                      <th>Water</th>
                      <th>Electricity</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(row, index) in filePreview.slice(0, 3)" :key="index">
                      <td><span class="dash-badge dash-badge--primary">{{ row.unit_number || '—' }}</span></td>
                      <td>{{ truncateText(row.tenant_name, 15) }}</td>
                      <td>{{ row.tenant_phone || '—' }}</td>
                      <td>{{ row.tenant_idnumber || '—' }}</td>
                      <td>{{ row.unit_type || '—' }}</td>
                      <td>{{ formatCurrency(row.monthly_rent) }}</td>
                      <td>
                        <span :class="getArrearsClass(row.arrears)">
                          {{ formatCurrency(row.arrears) }}
                        </span>
                      </td>
                      <td>{{ formatCurrency(row.wallet_amount) }}</td>
                      <td>{{ row.water_meter_readings || '—' }}</td>
                      <td>{{ row.electricity_meter_readings || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
                <p v-if="filePreview.length > 3" class="preview-more">
                  + {{ filePreview.length - 3 }} more records
                </p>
              </div>
            </div>

            <!-- UPLOAD BUTTON -->
            <button class="btn-upload" :disabled="!canUpload" @click="uploadData">
              <span v-if="loading" class="loading-spinner">
                <span class="spinner"></span>
                Processing...
              </span>
              <span v-else>
                <i class="bi bi-cloud-upload"></i>
                Upload Tenants & Units
              </span>
            </button>
          </div>
        </div>

        <!-- RIGHT COLUMN - RESULTS & SUMMARY -->
        <div class="summary-section">
          <!-- UPLOAD RESULTS -->
          <div v-if="uploadResult" class="results-card">
            <div class="results-header" :class="uploadResult.success ? 'success' : 'error'">
              <i :class="uploadResult.success ? 'bi bi-check-circle' : 'bi bi-exclamation-circle'"></i>
              <h3>{{ uploadResult.success ? 'Upload Successful!' : 'Upload Failed' }}</h3>
            </div>
            
            <!-- <div class="results-stats">
              <div class="stat-item">
                <span class="stat-value">{{ tenantsAdded || 0 }}</span>
                <span class="stat-label">Tenants Added</span>
              </div>
                 <div class="stat-item">
                <span class="stat-value">{{ tenantsUpdated || 0 }}</span>
                <span class="stat-label">Tenants Updates</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ unitsAdded || 0 }}</span>
                <span class="stat-label">Units Added</span>
              </div>
                <div class="stat-item">
                <span class="stat-value">{{ unitsUpdated || 0 }}</span>
                <span class="stat-label">Units Updated</span>
              </div>
              <div class="stat-item">
                <span class="stat-value">{{ (uploadResult.errors || []).length }}</span>
                <span class="stat-label">Errors</span>
              </div>
            </div> -->

            <!-- Financial Summary -->
            <div v-if="uploadResult.financial_summary" class="financial-summary">
              <h4><i class="bi bi-cash-stack"></i> Financial Summary</h4>
              <div class="summary-grid">
                <div class="summary-item">
                  <span class="summary-label">Total Rent:</span>
                  <span class="summary-value">{{ formatCurrency(uploadResult.financial_summary.total_rent) }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total Arrears:</span>
                  <span class="summary-value arrears">{{ formatCurrency(uploadResult.financial_summary.total_arrears) }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total Wallet:</span>
                  <span class="summary-value">{{ formatCurrency(uploadResult.financial_summary.total_wallet) }}</span>
                </div>
                <div class="summary-item">
                  <span class="summary-label">Total Deposits:</span>
                  <span class="summary-value">{{ formatCurrency(uploadResult.financial_summary.total_deposits) }}</span>
                </div>
              </div>
            </div>

            <div v-if="uploadResult.errors && uploadResult.errors.length" class="error-list">
              <h4><i class="bi bi-exclamation-triangle"></i> Errors</h4>
              <div v-for="(error, idx) in uploadResult.errors.slice(0, 3)" :key="idx" class="error-item">
                <span class="error-row">Row {{ error.row }}:</span>
                <span class="error-message">{{ error.message }}</span>
              </div>
              <p v-if="uploadResult.errors.length > 3" class="more-errors">
                + {{ uploadResult.errors.length - 3 }} more errors
              </p>
            </div>

            <button class="btn-clear" @click="clearUpload">
              <i class="bi bi-arrow-clockwise"></i>
              Upload More
            </button>
          </div>

          <!-- HOUSE STATS -->
          <div v-else class="stats-card">
            <div class="stats-header">
              <i class="bi bi-bar-chart"></i>
              <h3>House Summary</h3>
            </div>

            <div class="house-selector">
              <select v-model="statsHouseId" class="form-select" @change="loadHouseStats">
                <option value="">Select a house</option>
                <option v-for="house in houses" :key="house.id" :value="house.id">
                  {{ house.name }}
                </option>
              </select>
            </div>

            <div v-if="houseStats" class="house-stats">
              <div class="stats-grid">
                <div class="stat-box">
                  <span class="stat-number">{{ houseStats.total_units || 0 }}</span>
                  <span class="stat-label">Total Units</span>
                </div>
                <div class="stat-box">
                  <span class="stat-number">{{ houseStats.occupied_units || 0 }}</span>
                  <span class="stat-label">Occupied</span>
                </div>
                <div class="stat-box">
                  <span class="stat-number">{{ houseStats.vacant_units || 0 }}</span>
                  <span class="stat-label">Vacant</span>
                </div>
              </div>

              <div class="stats-details">
                <div class="detail-item">
                  <span class="detail-label">Total Tenants:</span>
                  <span class="detail-value">{{ houseStats.total_tenants || 0 }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Monthly Rent:</span>
                  <span class="detail-value">{{ formatCurrency(houseStats.total_monthly_rent) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Total Arrears:</span>
                  <span class="detail-value arrears">{{ formatCurrency(houseStats.total_arrears) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Wallet Balance:</span>
                  <span class="detail-value">{{ formatCurrency(houseStats.total_wallet) }}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Deposits:</span>
                  <span class="detail-value">{{ formatCurrency(houseStats.total_deposits) }}</span>
                </div>
              </div>
            </div>

            <div v-else-if="statsHouseId" class="no-stats">
              <i class="bi bi-inbox"></i>
              <p>No data available for this house</p>
            </div>
          </div>

          <!-- QUICK INFO -->
          <div class="info-card">
            <i class="bi bi-lightbulb"></i>
            <div>
              <h4>How it works</h4>
              <p>Upload a CSV file with tenant and unit information. The system will:</p>
              <ul>
                <li>Create new tenants if they don't exist</li>
                <li>Assign tenants to units</li>
                <li>Update unit occupancy and type</li>
                <li>Record financials (rent, arrears, wallet, deposits)</li>
                <li>Save utility meter readings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { apiFetch } from "@/services/api.js";
import Papa from 'papaparse';

export default {
  name: "BulkUploadTenantsUnits",
  
  data() {
    return {
      houses: [],
      selectedHouseId: "",
      statsHouseId: "",
      file: null,
      filePreview: [],
      loading: false,
      uploadResult: null,
      houseStats: null
    };
  },

  computed: {
    canUpload() {
      return this.selectedHouseId && this.file && this.filePreview.length > 0 && !this.loading;
    }
  },

  async mounted() {
    await this.loadHouses();
  },

  methods: {
    async loadHouses() {
      try {
        const res = await apiFetch("/houses");
        this.houses = Array.isArray(res) ? res : res.data || [];
      } catch (error) {
        console.error("Error loading houses:", error);
        this.showToast("error", "Failed to load houses");
      }
    },

    async loadHouseStats() {
      if (!this.statsHouseId) {
        this.houseStats = null;
        return;
      }

      try {
        const res = await apiFetch(`/houses/${this.statsHouseId}/stats`);
        this.houseStats = res.data || res;
      } catch (error) {
        console.error("Error loading house stats:", error);
        this.showToast("error", "Failed to load house statistics");
      }
    },

    async downloadTemplate() {
      try {
        // CHANGED: Unit information now comes FIRST in the CSV
        const headers = [
          // Unit Information (FIRST)
          'unit_number', 'unit_type', 'monthly_rent', 'service_charge', 'other_charges', 'deposit_paid',
          
          // Tenant Information (SECOND)
          'tenant_name', 'tenant_phone', 'tenant_idnumber', 'gender', 'marital_status', 'move_in_date',
          
          // Financial Information
          'arrears', 'wallet_amount',
          
          // Utilities
          'water_meter_readings', 'electricity_meter_readings'
        ];
        
        // Sample rows with unit data first, tenant data second
        const sampleData = [[
          // Unit Information (FIRST)
          'A1', '2 Bedroom', '15000', '500', '200', '15000',
          
          // Tenant Information (SECOND)
          'Ronnie', '712345678', '12345678', 'Male', 'Married', '2026-03-01',
          
          // Financial Information
          '0', '0',
          
          // Utilities
          '10', '10'
        ]];
        
        let csvContent = headers.join(',') + '\n';
        sampleData.forEach(row => {
          csvContent += row.join(',') + '\n';
        });
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        
        const a = document.createElement("a");
        a.href = url;
        a.download = "units_tenants_template.csv";
        document.body.appendChild(a);
        a.click();
        
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        this.showToast("success", "Template downloaded!");
      } catch (error) {
        console.error("Template download error:", error);
        this.showToast("error", "Download failed");
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    handleFile(e) {
      const file = e.target.files[0];
      if (file) {
        this.file = file;
        this.previewFile(file);
      }
    },

    handleDrop(e) {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files.length && files[0].name.endsWith('.csv')) {
        this.file = files[0];
        this.previewFile(this.file);
      }
    },

    previewFile(file) {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          this.filePreview = results.data.filter(row => 
            row.unit_number && row.tenant_name && row.tenant_idnumber
          );
        },
        error: (error) => {
          console.error("CSV parse error:", error);
          this.showToast("error", "Invalid CSV format");
        }
      });
    },

    removeFile() {
      this.file = null;
      this.filePreview = [];
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    async uploadData() {
      if (!this.canUpload) return;

      const formData = new FormData();
      formData.append("file", this.file);
      formData.append("house_id", this.selectedHouseId);

      try {
        this.loading = true;
        this.uploadResult = null;

        const res = await apiFetch("/bulk/upload-tenants-units", {
          method: "POST",
          body: formData,
          headers: {}
        });

        this.uploadResult = res;

        if (res.success) {
            // ✅ FIX: Access the counts from res.data instead of res directly
      const tenantsAdded = res.data?.tenants_added || 0;
      const unitsUpdated = res.data?.units_updated || 0;
    const unitsAdded = res.data?.units_added || 0;

    const tenantsUpdated = res.data?.tenants_updated || 0;

 
          this.showToast("success", `Summary: ${tenantsAdded} Tenants added,  ${tenantsUpdated}  tenants updated,  ${unitsAdded} units Added and  ${unitsUpdated} units updated to your apartment`);
          if (this.statsHouseId === this.selectedHouseId) {
            await this.loadHouseStats();
          }
        } else {
          this.showToast("error", res.message || "Upload failed");
        }

      } catch (error) {
        console.error("Upload error:", error);
        this.uploadResult = {
          success: false,
          message: error.message || "Upload failed",
          errors: [{ message: error.message }]
        };
        this.showToast("error", error.message || "Upload failed");
      } finally {
        this.loading = false;
      }
    },

    clearUpload() {
      this.file = null;
      this.filePreview = [];
      this.uploadResult = null;
      this.selectedHouseId = "";
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    },

    formatCurrency(value) {
      if (!value || value === '—') return '—';
      const num = parseFloat(value);
      if (isNaN(num)) return '—';
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    },

    truncateText(text, length) {
      if (!text) return '—';
      return text.length > length ? text.substring(0, length) + '...' : text;
    },

    getGenderBadgeClass(gender) {
      if (!gender) return 'badge-neutral';
      return gender.toLowerCase() === 'male' ? 'badge-male' : 'badge-female';
    },

    getArrearsClass(arrears) {
      const num = parseFloat(arrears);
      if (isNaN(num) || num <= 0) return 'arrears-none';
      return 'arrears-positive';
    },

    showToast(type, message) {
      // Replace with your toast implementation
      alert(`${type.toUpperCase()}: ${message}`);
    }
  }
};
</script>

<style scoped>
/* All your existing styles remain exactly the same */
.page-wrapper {
  background: var(--dash-bg);
  min-height: 100vh;
}

.bulk-upload-container {
  max-width: 1600px;
  margin: 0 auto;
  padding: 30px 25px;
}

/* Header Section */
.header-section {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 25px 30px;
  margin-bottom: 30px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.header-content {
  display: flex;
  align-items: center;
  gap: 20px;
}

.header-icon {
  width: 60px;
  height: 60px;
  background: var(--dash-primary-soft);
  border-radius: var(--dash-radius-lg);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-primary-ink);
}

.header-icon i {
  font-size: 1.75rem;
  color: var(--dash-primary-ink);
}

.header-title {
  font-size: var(--dash-fs-h1);
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 5px 0;
}

.header-subtitle {
  color: var(--dash-muted);
  margin: 0;
  font-size: 16px;
}

.btn-template {
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: var(--dash-pill);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.3s ease;
  box-shadow: var(--dash-shadow);
}

.btn-template:hover {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 25px;
}

/* Upload Section */
.upload-section {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 25px;
}

.card-header i {
  font-size: 1.05em;
  color: var(--dash-primary-ink);
  background: var(--dash-primary-soft);
  padding: 12px;
  border-radius: var(--dash-radius);
}

.card-header h3 {
  font-size: 22px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0;
}

/* Form Elements */
.form-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  color: var(--dash-text);
  margin-bottom: 8px;
}

.form-label i {
  color: var(--dash-muted);
}

.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--dash-line);
  border-radius: var(--dash-radius);
  font-size: 15px;
  background: var(--dash-surface-2);
  transition: all 0.3s ease;
  margin-bottom: 20px;
}

.form-select:focus {
  outline: none;
  border-color: var(--dash-primary);
  background: white;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* File Upload */
.file-upload-area {
  margin-bottom: 25px;
}

.file-drop-zone {
  border: 2px dashed var(--dash-line-strong);
  border-radius: var(--dash-radius-lg);
  padding: 40px 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.file-drop-zone:hover {
  border-color: var(--dash-primary);
  background: var(--dash-surface-2);
}

.drop-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
}

.drop-icon {
  width: 70px;
  height: 70px;
  background: var(--dash-bg-alt);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.drop-icon i {
  font-size: 1.75rem;
  color: var(--dash-muted);
}

.drop-text {
  font-size: 16px;
  color: var(--dash-text);
  margin: 0;
}

.browse-link {
  color: var(--dash-primary);
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
}

.drop-hint {
  font-size: 14px;
  color: var(--dash-faint);
  margin: 5px 0 0 0;
}

.file-preview {
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  border: 1px solid var(--dash-line);
}

.file-icon {
  width: 40px;
  height: 40px;
  background: var(--dash-success-soft);
  border-radius: var(--dash-radius-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--dash-success-ink);
}

.file-icon i {
  font-size: 20px;
  color: var(--dash-success-ink);
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-name {
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0;
}

.file-info {
  color: var(--dash-muted);
  font-size: 13px;
  margin: 4px 0 0 0;
}

.btn-remove-file {
  background: none;
  border: none;
  color: var(--dash-danger);
  cursor: pointer;
  padding: 8px;
  border-radius: var(--dash-pill);
  transition: all 0.2s;
}

.btn-remove-file:hover {
  background: var(--dash-danger-soft);
}

.d-none {
  display: none;
}

/* Format Guide */
.format-guide {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-lg);
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid var(--dash-line);
}

.format-guide h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0 0 5px 0;
}

.format-guide p {
  color: var(--dash-muted);
  font-size: 14px;
  margin-bottom: 15px;
}

.required-star {
  color: var(--dash-danger);
  font-weight: bold;
}

.columns-categories {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.category-section h5 {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: var(--dash-ink);
  margin: 0 0 10px 0;
}

.category-section h5 i {
  color: var(--dash-muted);
}

.columns-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.column-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  background: white;
  border-radius: var(--dash-radius-lg);
  font-size: 12px;
  font-weight: 500;
  color: var(--dash-text);
  border: 1px solid var(--dash-line);
}

.column-tag.required {
  background: var(--dash-primary-soft);
  border-color: var(--dash-primary-ink);
  color: var(--dash-primary-hover);
}

.column-tag i {
  font-size: 10px;
}

/* Preview Section */
.preview-section {
  margin-bottom: 25px;
  overflow-x: auto;
}

.preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-text);
  font-weight: 600;
  margin-bottom: 15px;
}

.preview-table-container {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
  overflow-x: auto;
  border: 1px solid var(--dash-line);
}

.preview-table {
  min-width: 1200px;
}

.preview-table th,
.preview-table td {
  white-space: nowrap;
}

.preview-more {
  padding: 12px;
  text-align: center;
  color: var(--dash-muted);
  font-size: 13px;
  border-top: 1px solid var(--dash-line);
}

/* Badges */
.badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: var(--dash-radius-lg);
  font-size: 11px;
  font-weight: 600;
  text-transform: capitalize;
}

.badge-neutral {
  background: var(--dash-line);
  color: var(--dash-text);
}

.badge-male {
  background: var(--dash-primary);
  color: white;
}

.badge-female {
  background: var(--dash-alt);
  color: white;
}

.arrears-none {
  color: var(--dash-success);
  font-weight: 500;
}

.arrears-positive {
  color: var(--dash-danger);
  font-weight: 600;
}

/* Upload Button */
.btn-upload {
  width: 100%;
  background: var(--dash-primary);
  color: white;
  border: none;
  padding: 16px;
  border-radius: var(--dash-pill);
  font-weight: 700;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s ease;
  box-shadow: var(--dash-shadow);
}

.btn-upload:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: var(--dash-shadow);
}

.btn-upload:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.loading-spinner {
  display: flex;
  align-items: center;
  gap: 10px;
}

.spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Right Column */
.summary-section {
  display: flex;
  flex-direction: column;
  gap: 25px;
}

/* Results Card */
.results-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.results-header {
  text-align: center;
  margin-bottom: 25px;
}

.results-header i {
  font-size: 2.25rem;
  margin-bottom: 15px;
}

.results-header.success {
  color: var(--dash-success);
}

.results-header.error {
  color: var(--dash-danger);
}

.results-header h3 {
  font-size: 20px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0;
}

.results-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 15px;
  margin-bottom: 25px;
}

.stat-item {
  text-align: center;
  padding: 15px;
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius);
}

.stat-value {
  display: block;
  font-size: var(--dash-fs-figure);
  font-weight: 700;
  color: var(--dash-ink);
  margin-bottom: 5px;
}

.stat-label {
  font-size: 12px;
  color: var(--dash-muted);
}

/* Financial Summary */
.financial-summary {
  background: var(--dash-primary-soft);
  border-radius: var(--dash-radius);
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid var(--dash-primary-line);
}

.financial-summary h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-primary-ink);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 15px 0;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  padding: 10px;
  background: white;
  border-radius: var(--dash-radius-xs);
}

.summary-label {
  font-size: 11px;
  color: var(--dash-muted);
  margin-bottom: 4px;
}

.summary-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--dash-ink);
}

.summary-value.arrears {
  color: var(--dash-danger);
}

/* Error List */
.error-list {
  background: var(--dash-danger-soft);
  border-radius: var(--dash-radius);
  padding: 20px;
  margin-bottom: 25px;
  border: 1px solid var(--dash-danger-soft);
}

.error-list h4 {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--dash-danger-ink);
  font-size: 16px;
  font-weight: 700;
  margin: 0 0 15px 0;
}

.error-item {
  display: flex;
  gap: 10px;
  padding: 8px 0;
  border-bottom: 1px solid var(--dash-danger-soft);
  font-size: 13px;
}

.error-item:last-child {
  border-bottom: none;
}

.error-row {
  font-weight: 600;
  color: var(--dash-danger-ink);
  min-width: 60px;
}

.error-message {
  color: var(--dash-danger-ink);
}

.more-errors {
  color: var(--dash-muted);
  font-size: 13px;
  margin: 10px 0 0 0;
}

.btn-clear {
  width: 100%;
  padding: 12px;
  background: var(--dash-line);
  color: var(--dash-text);
  border: none;
  border-radius: var(--dash-pill);
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
}

.btn-clear:hover {
  background: var(--dash-line-strong);
}

/* Stats Card */
.stats-card {
  background: white;
  border-radius: var(--dash-radius-lg);
  padding: 30px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
}

.stats-header i {
  font-size: 24px;
  color: var(--dash-primary-ink);
  background: var(--dash-primary-soft);
  padding: 10px;
  border-radius: var(--dash-radius-sm);
}

.stats-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: var(--dash-ink);
  margin: 0;
}

.house-selector {
  margin-bottom: 25px;
}

.house-stats {
  background: var(--dash-surface-2);
  border-radius: var(--dash-radius-lg);
  padding: 20px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.stat-box {
  text-align: center;
  padding: 15px;
  background: white;
  border-radius: var(--dash-radius);
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.stat-box .stat-number {
  display: block;
  font-size: 24px;
  font-weight: 700;
  color: var(--dash-ink);
  margin-bottom: 4px;
}

.stat-box .stat-label {
  font-size: 11px;
  color: var(--dash-muted);
}

.stats-details {
  border-top: 1px solid var(--dash-line);
  padding-top: 15px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
}

.detail-label {
  color: var(--dash-muted);
}

.detail-value {
  font-weight: 600;
  color: var(--dash-ink);
}

.detail-value.arrears {
  color: var(--dash-danger);
}

.no-stats {
  text-align: center;
  padding: 40px 20px;
  color: var(--dash-faint);
}

.no-stats i {
  font-size: 2.25rem;
  margin-bottom: 15px;
}

.no-stats p {
  margin: 0;
}

/* Info Card */
.info-card {
  background: var(--dash-primary);
  border-radius: var(--dash-radius-lg);
  padding: 25px;
  color: white;
  display: flex;
  gap: 20px;
  box-shadow: var(--dash-shadow);
}

.info-card i {
  font-size: 2rem;
  opacity: 0.9;
}

.info-card h4 {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 10px 0;
}

.info-card p {
  margin: 0 0 15px 0;
  font-size: 14px;
  opacity: 0.95;
}

.info-card ul {
  margin: 0;
  padding-left: 20px;
  font-size: 13px;
  opacity: 0.9;
}

.info-card li {
  margin-bottom: 5px;
}

/* Responsive */
@media (max-width: 1400px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .header-section {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
  }
  
  .columns-grid {
    gap: 5px;
  }
  
  .column-tag {
    font-size: 10px;
    padding: 3px 6px;
  }
  
  .results-stats {
    grid-template-columns: 1fr;
  }
  
  .summary-grid {
    grid-template-columns: 1fr;
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
  }
  
  .info-card {
    flex-direction: column;
    text-align: center;
  }
  
  .info-card ul {
    text-align: left;
  }
}
</style>